// To update the drivebase SVG:
// 1) Convert the HTML formatted SVG file to a React friendly Javascript format.
// 2) From the command line: npx @svgr/cli my_new_drivebase.svg > MyDrivebase.jsx
// 3) Cut everything inside the <svg></svg> (remove any <defs> sections)
//    from the newly formatted jsx file and paste (replace) in DrivebaseControl.tsx.
//    Leave everything outside the <svg></svg> tags already in DrivebaseControl.tsx
// 4) Do a search for <filename__> in className="<filename__>controller-button".
//    Replace <filename__> with empty space for all instances. The conversion
//    process adds the file name to many tags.
import React from 'react';
import '../Controller/Controller.css';
import { editor as MonacoEditor } from 'monaco-editor';
import { useColorScheme } from '@mui/material/styles';
import ButtonMappingDialog from '../Controller/ButtonMappingDialog';
import { useMappingUi } from '../Controller/useMappingUi';
import { INPUT_BINDING_DEVICE } from '../../../utils/inputBindings';

const { DRIVEBASE } = INPUT_BINDING_DEVICE;

interface DrivebaseControlProps {
  editor?: MonacoEditor.IStandaloneCodeEditor;
  /** When false, SVG clicks do not open the mapping dialog (Tree-sitter not ready).
   * When omitted, falls back to live Tree-sitter readiness. */
  mappingsReady?: boolean;
}

const DrivebaseControl: React.FC<DrivebaseControlProps> = ({ editor, mappingsReady }) => {
  const { mode, systemMode } = useColorScheme();
  const visualMode = (mode === 'system' ? systemMode || 'light' : mode) as 'light' | 'dark';
  const {
    svgRef,
    dialogOpen,
    dialogButton,
    setDialogOpen,
    handleSvgMouseMove,
    handleSvgMouseLeave,
    handleSvgClick,
    handleSaveButtonBindings,
    mappingsReady: resolvedMappingsReady,
  } = useMappingUi(DRIVEBASE, editor, mappingsReady);

  // interactions and save handled by useMappingUi

  // Inline the prepared SVG (converted to JSX) here or import a React component version.
  // For now, we assume the drivebase SVG has been converted and accepts ref/handlers.
  // If the SVG is left as raw, paste its JSX here replacing the <svg> root props with the below.

  return (
    <React.Fragment>
      <div className={`controller-container ${visualMode === 'dark' ? 'dark' : 'light'}`}>
        {/* Replace the following dummy svg with the actual drivebase SVG content converted to JSX */}
        {/* The important part is adding ref/onMouseMove/onMouseLeave and data-button attributes on clickable groups */}
        <svg
          viewBox="0 0 320 140"
          id="svg1"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          preserveAspectRatio="xMidYMid meet"
          xmlSpace="preserve"
          ref={svgRef}
          onMouseMove={handleSvgMouseMove}
          onMouseLeave={handleSvgMouseLeave}
          onClick={handleSvgClick}
        >
          <g
            id="g85"
            style={{
              display: 'inline',
              stroke: '#000101',
              strokeOpacity: 1,
            }}
            transform="translate(-236.68 -229.952)"
          >
            <g
              id="g66"
              style={{
                display: 'inline',
                stroke: '#000101',
                strokeOpacity: 1,
              }}
            >
              <path
                id="path28"
                d="m258.94 348.29.52.41.63.21.33.03c45.6.03 91.2.05 136.8.05.27 0 .45-.04.63-.1l.6-.3.48-.5.18-.31v-99l-.14-.76-.41-.64-.61-.43-.25-.08-.21-.04-.25-.03h-15.76l-7.6.01h-8.42c-4.98 0-9.95 0-14.92-.01l-56.11-.15c-11.4-.04-22.74-.09-34.03-.15h-.26c-.4.06-.8.22-1.08.5l-.4.6-.14.69v99.02l.09.5.32.48z"
                className="cls-2"
                style={{
                  fill: 'none',
                  fillOpacity: 1,
                  stroke: '#000101',
                  strokeWidth: 2.2,
                  strokeDasharray: 'none',
                  strokeOpacity: 1,
                }}
              />
              <path
                id="path30"
                d="M238.38 260.01a794 794 0 0 0 4.45 58.8c.28 2.44.57 4.88.86 7.31.3 2.44.61 4.87.92 7.31l1.85 14.4.1.19-.02.31 3.46 19.61.11.37.47.57.73.27h.4l17.91.06c14.02.04 28.03.08 42.05.11 28.03.06 56.06.1 84.09.11 24.65 0 49.29-.04 73.94-.08l70.48-.19.34-.04.32-.11.52-.42.25-.58v0c.51-2.83 1-5.66 1.48-8.49.48-2.84.93-5.67 1.37-8.51.88-5.65 1.7-11.4 2.45-17.08 1.51-11.35 2.79-22.9 3.8-34.33 1.03-11.59 1.8-23.2 2.31-34.49.25-5.66.44-11.47.56-17.29.05-2.53.09-5.06.13-7.59l.13-9.14c-105.35-.05-210.7-.05-316.06 0 .02 4.73.05 9.45.14 14.18.09 4.92.24 9.84.43 14.76zm14.92 14.15c.01-8.34.03-16.69.06-25.06v0-.01 0-.02c0-.91.19-1.8.54-2.64.18-.42.4-.82.65-1.2.26-.38.55-.74.88-1.06s.67-.61 1.06-.86c.38-.25.79-.47 1.21-.64.84-.34 1.76-.53 2.66-.53v0c45.61.08 91.23.12 136.85.12.92 0 1.82.18 2.67.54a6.944 6.944 0 0 1 3.13 2.63c.25.38.47.8.64 1.22.35.86.52 1.77.53 2.7v99.88c-.022.442-.018.564-.05.87-.05.74-.19 1.46-.49 2.14-.17.4-.39.8-.64 1.18-.25.37-.55.72-.88 1.03-.32.31-.67.59-1.06.84-.38.24-.79.46-1.21.63-.83.33-1.75.51-2.66.51h-1.43c-22.09 0-44.63-.07-67.03-.19-22.82-.13-45.63-.33-68.44-.57-.91-.01-1.8-.19-2.65-.54-.43-.18-.83-.39-1.2-.64-.38-.25-.74-.54-1.06-.86a7 7 0 0 1-.87-1.04c-.26-.38-.48-.78-.65-1.2-.36-.84-.55-1.76-.55-2.66v0c-.04-16.52-.06-33.04-.06-49.64l.02-24.95z"
                className="cls-2"
                style={{
                  display: 'inline',
                  fill: 'none',
                  fillOpacity: 1,
                  stroke: '#000101',
                  strokeWidth: 2.2,
                  strokeDasharray: 'none',
                  strokeOpacity: 1,
                }}
              />
            </g>
          </g>
          <g
            id="back_button"
            className="controller-button"
            data-button="back"
            style={{
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 39.775 -174.37)"
          >
            <circle
              id="circle93"
              cx={65.799}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.596106,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path92"
              d="M485.53 345.38c-1.18 0-2.27.3-3.2.8-.17-.42-.28-.8-.31-1.1a.535.535 0 0 0-.61-.4c-.11.02-.21.07-.32.19l-3.84 4.98c-.11.14-.15.27-.14.4v.04q0 0 0 0c0 .08.03.16.07.23.07.12.17.2.29.24.09.03.2.05.33.03l6.22-.92c.16-.03.25-.1.32-.19a.54.54 0 0 0-.05-.73c-.27-.19-.57-.5-.88-.89.62-.34 1.33-.55 2.13-.55 2.48 0 4.45 2.05 4.45 4.64s-1.97 4.62-4.45 4.62c-1.35 0-2.48-.58-3.27-1.47-.3-.26-.6-.43-.88-.43-.62 0-1.11.49-1.11 1.09 0 .24.09.55.3.75 1.15 1.32 2.91 2.2 4.96 2.2 3.76 0 6.73-2.99 6.73-6.77s-2.97-6.77-6.73-6.77z"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                strokeWidth: 1.76,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
              }}
              transform="matrix(.26458 0 0 .26458 -62.422 -14.706)"
            />
          </g>
          <g
            id="up_button"
            className="controller-button"
            data-button="up"
            style={{
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 -15.37 -206.861)"
          >
            <circle
              id="path1-2-3-3-4"
              cx={65.846}
              cy={-62.571}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.596106,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
              transform="scale(1 -1)"
            />
            <path
              id="path60-3"
              d="m67.246 63.605-1.124-2.791a.3.3 0 0 0-.132-.156l-.016-.008q0 0 0 0a.3.3 0 0 0-.111-.027.26.26 0 0 0-.17.061.3.3 0 0 0-.092.13l-1.128 2.791a.255.255 0 0 0 .315.333c.23-.105.625-.177 1.072-.177s.841.072 1.071.177a.256.256 0 0 0 .315-.333"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.590815,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="down_button"
            className="controller-button"
            data-button="down"
            style={{
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 -15.37 -206.861)"
          >
            <circle
              id="path1-2-3-3-4-1-3"
              cx={65.799}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.596106,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path61-6"
              d="m64.473 77.302 1.125 2.792c.032.077.08.127.132.156l.016.008v0a.3.3 0 0 0 .111.026.27.27 0 0 0 .17-.06.3.3 0 0 0 .092-.13l.992-2.458.135-.334a.255.255 0 0 0-.315-.333c-.23.106-.624.177-1.071.177s-.842-.071-1.072-.177a.256.256 0 0 0-.315.333"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.590815,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="right_button"
            className="controller-button"
            data-button="right"
            style={{
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 -15.37 -206.861)"
          >
            <circle
              id="path1-2-3-3"
              cx={73.72}
              cy={70.443}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.596106,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path62-7"
              d="m72.71 71.89 2.791-1.125a.3.3 0 0 0 .156-.133l.008-.016q0 0 0 0a.3.3 0 0 0 .027-.11.26.26 0 0 0-.061-.17.3.3 0 0 0-.13-.093l-2.791-1.127a.255.255 0 0 0-.333.315c.105.23.177.625.177 1.072s-.072.841-.177 1.071a.256.256 0 0 0 .333.315"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.590815,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="left_button"
            className="controller-button"
            data-button="left"
            style={{
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 -15.37 -206.861)"
          >
            <circle
              id="path1-2-3-3-4-1"
              cx={57.834}
              cy={70.485}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.596106,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path63-6"
              d="m58.962 69.116-2.791 1.125a.3.3 0 0 0-.156.132l-.008.016q0 0 0 0a.3.3 0 0 0-.027.111c0 .066.024.124.061.17a.3.3 0 0 0 .13.092l2.791 1.127a.255.255 0 0 0 .333-.315c-.105-.23-.177-.624-.177-1.071s.072-.842.178-1.072a.256.256 0 0 0-.334-.315"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.590815,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g
            id="select_button"
            className="controller-button"
            data-button="select"
            style={{
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(3.77953 0 0 3.77953 -.527 -174.345)"
          >
            <circle
              id="circle96"
              cx={65.752}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.596106,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path91"
              d="M522.69 350.8c-.11-.26-.37-.53-.63-.63a1.04 1.04 0 0 0-1.32.62c-.08.21-.09.43 0 .72l2.56 6.24a1.26 1.26 0 0 0 .6.67c.14.07.29.11.46.11.26 0 .5-.09.68-.25.15-.12.28-.29.37-.52l4.57-11.32c.09-.29.08-.51 0-.72a1.04 1.04 0 0 0-1.32-.62c-.27.1-.53.37-.63.63l-3.65 8.92-1.68-3.84z"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000100',
                strokeWidth: 1.76,
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="matrix(.26458 0 0 .26458 -73.206 -14.69)"
            />
          </g>
          <path
            id="path21-7"
            d="M294.177 38.035c-6.023 0-10.911-4.888-10.911-10.912 0-6.023 4.888-10.911 10.911-10.911 6.024 0 10.919 4.888 10.919 10.911 0 6.024-4.888 10.912-10.919 10.912zm0-24.937h-14.19c-.466 0-.61.392-.188.625.586.33 3.143 1.79 3.143 1.79 1.752 1.06.459 2.646.459 2.646a13.93 13.93 0 0 0-3.249 8.964c0 7.746 6.28 14.025 14.025 14.025 7.746 0 14.025-6.28 14.025-14.025s-6.28-14.025-14.025-14.025z"
            style={{
              display: 'inline',
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 2.26394,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path22"
            d="M302.825 27.123c0 1.76-.978 3.181-2.18 3.181-1.204 0-2.182-1.429-2.182-3.18 0-1.753.978-3.182 2.181-3.182s2.181 1.43 2.181 3.181z"
            style={{
              display: 'inline',
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 2.26394,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
          <path
            id="path23-7"
            d="M296.35 27.123c0 2.467-1.368 4.467-3.06 4.467s-3.061-2-3.061-4.467 1.369-4.467 3.06-4.467c1.693 0 3.061 2 3.061 4.467z"
            style={{
              display: 'inline',
              fill: 'none',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 2.26394,
              strokeDasharray: 'none',
              strokeOpacity: 1,
            }}
          />
        </svg>

        <ButtonMappingDialog
          open={dialogOpen}
          device={DRIVEBASE}
          onClose={() => setDialogOpen(false)}
          onSave={handleSaveButtonBindings}
          initialButton={dialogButton}
          mappingsReady={resolvedMappingsReady}
        />
      </div>
    </React.Fragment>
  );
};

export default DrivebaseControl;
